import React, { useEffect, useState } from 'react';
import { getSuppliers, createSupplier, updateSupplier, toggleSupplierActive } from '../lib/api';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Power, Loader2 } from 'lucide-react';

export default function SuppliersPage() {
  const { t } = useI18n();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', country: '', address: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadSuppliers(); }, []);

  const loadSuppliers = async () => {
    try {
      const res = await getSuppliers();
      setSuppliers(res.data);
    } catch (err) {
      toast.error(t('entities.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error(t('common.required')); return; }
    setSaving(true);
    try {
      if (editing) {
        await updateSupplier(editing.id, form);
        toast.success(t('common.updatedOk'));
      } else {
        await createSupplier(form);
        toast.success(t('common.createdOk'));
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: '', country: '', address: '' });
      loadSuppliers();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('entities.errorSaving'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditing(supplier);
    setForm({ name: supplier.name, country: supplier.country || '', address: supplier.address || '' });
    setShowForm(true);
  };

  const handleToggleActive = async (supplierId) => {
    try {
      await toggleSupplierActive(supplierId);
      loadSuppliers();
      toast.success(t('entities.stateChanged'));
    } catch (err) {
      toast.error(t('entities.errorToggle'));
    }
  };

  const resetForm = () => {
    setEditing(null);
    setForm({ name: '', country: '', address: '' });
  };

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="suppliers-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl">{t('entities.suppliers')}</h1>
            <p className="text-muted-foreground">{suppliers.length} {t('entities.suppliers').toLowerCase()}</p>
          </div>
          <Dialog open={showForm} onOpenChange={(open) => { setShowForm(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-supplier-btn">
                <Plus size={16} className="mr-2" />
                {t('entities.addSupplier')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editing ? t('entities.editSupplier') : t('entities.addSupplier')}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>{t('common.name')} *</Label>
                    <Input
                      value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      placeholder={t('common.name')}
                      className="mt-1"
                      data-testid="supplier-name-input"
                    />
                  </div>
                  <div>
                    <Label>{t('shipments.origin')}</Label>
                    <Input
                      value={form.country}
                      onChange={(e) => setForm({...form, country: e.target.value})}
                      className="mt-1"
                      placeholder="China, USA, etc."
                    />
                  </div>
                </div>
                <div>
                  <Label>{t('entities.address')}</Label>
                  <Textarea
                    value={form.address}
                    onChange={(e) => setForm({...form, address: e.target.value})}
                    placeholder="Direccion completa del proveedor"
                    className="mt-1"
                    rows={2}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
                  <Button type="submit" disabled={saving} data-testid="save-supplier-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t('common.save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full dense-table">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b border-border bg-secondary/30">
                <th className="py-2 px-3 font-medium">{t('common.name')}</th>
                <th className="py-2 px-3 font-medium">{t('shipments.origin')}</th>
                <th className="py-2 px-3 font-medium">{t('entities.address')}</th>
                <th className="py-2 px-3 font-medium">{t('common.status')}</th>
                <th className="py-2 px-3 font-medium w-24">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('common.loading')}</td></tr>
              ) : suppliers.length === 0 ? (
                <tr><td colSpan={5} className="py-8 text-center text-muted-foreground">{t('entities.noSuppliers')}</td></tr>
              ) : (
                suppliers.map((supplier) => (
                  <tr key={supplier.id} className="border-b border-border/50 hover:bg-secondary/30" data-testid={`supplier-row-${supplier.id}`}>
                    <td className="py-2 px-3 font-medium">{supplier.name}</td>
                    <td className="py-2 px-3 text-sm text-muted-foreground">{supplier.country || '—'}</td>
                    <td className="py-2 px-3 text-sm text-muted-foreground truncate max-w-[200px]">{supplier.address || '—'}</td>
                    <td className="py-2 px-3">
                      <span className={`text-xs px-2 py-0.5 rounded ${supplier.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                        {supplier.is_active ? t('users.active') : t('users.inactive')}
                      </span>
                    </td>
                    <td className="py-2 px-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEdit(supplier)}>
                          <Edit2 size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => handleToggleActive(supplier.id)} className={supplier.is_active ? 'text-amber-400' : 'text-emerald-400'}>
                          <Power size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
}
